import { Trans } from '@lingui/react'
import { clsx } from 'clsx'
import { arrayOf, bool, func, number, shape, string } from 'prop-types'
import { Component, Fragment } from 'react'

import { PAGE_TYPE_SELLER_PRODUCTS, TENANT_IT } from 'shared/consts'
import { GTM_EVENT_TYPE_CLICK } from 'shared/consts/gtm'
import {
  PAGE_TYPE_BRAND_PAGE,
  PAGE_TYPE_CATEGORY,
  PAGE_TYPE_SEARCH_RESULTS,
} from 'shared/consts/page'
import LoadMoreButtonExperiment from 'shared/experiments/components/SerpLoadMore'
import {
  EXPERIMENT_VARIATION,
  NEW_SEARCH_PAGE_LAYOUT_V3,
} from 'shared/experiments/consts'
import { getExperiment } from 'shared/experiments/utils/experiments'
import { isMarketplace } from 'shared/experiments/utils/featureFlags'
import { isRebrandingITActive } from 'shared/experiments/utils/rebranding'
import store from 'shared/store'
import {
  REFRESH_MODALS,
  SEARCH_RESULTS_HITS_UPDATED,
} from 'shared/store/ducks/events'
import {
  isAdServerBannerExperimentEnabled,
  reorderHitsBySponsoredProduct,
} from 'shared/utils/adServerUtils'
import { getSortingItems } from 'shared/utils/algolia/filters'
import { getProperty } from 'shared/utils/algolia/resultsState'
import noop from 'shared/utils/noop'
import AdBanner from 'views/components/molecules/AdBanner'
import AdSlot from 'views/components/molecules/AdSlot'
import Modal from 'views/components/molecules/Modal'
import OdsModalContent from 'views/components/organisms/BuyBoxWithVariants/BuyBoxOdsButtonAndModal/OdsModalContent'
import CompactProductListItem from 'views/components/organisms/FilteredProductList/CompactProductListItem'
import { IMAGE_LAZY_LOAD_START_INDEX } from 'views/components/organisms/FilteredProductList/consts'
import FilteredProductListItem from 'views/components/organisms/FilteredProductList/FilteredProductListItem'
import SearchProductListItem from 'views/components/organisms/FilteredProductList/SearchProductListItem'
import OdsTransitionModal from 'views/components/organisms/OdsTransitionModal'
import SellerModal from 'views/components/organisms/SellerModal'
import {
  AD_SLOT_SEARCH_HITS_1,
  AD_SLOT_SEARCH_HITS_2,
  AD_SLOT_SEARCH_TOP_BANNER,
  ALGOLIA_SEARCH_LIST_HOOK,
  ALGOLIA_SEARCH_LIST_SELECTOR,
  HITS_BASE_CLASSNAME,
} from 'views/consts'
import {
  isProductAvailable,
  isProductOutOfStock,
} from 'views/utils/productUtils'
import { i18nType, publicRuntimeConfigType } from 'views/utils/propTypes'
import { hitsType } from 'views/utils/propTypes/hits'

import {
  convertAlgoliaHitToProductProps,
  getAlgoliaUserDataProductsByType,
} from './helpers'

import './Hits.scss'

export class Hits extends Component {
  state = this.getInitialState()

  constructor(props) {
    super(props)
    this.filteredItems = []
    this.enableSponsoredLabel = true
  }

  getInitialState() {
    const {
      publicRuntimeConfig: {
        pageProperties: {
          resultsState,
          adServerMetadata = {},
          injectedSponsoredProducts = [],
        },
      },
    } = this.props

    // Sponsored products should they the same, regardless of filtering or navigation
    const userData = getProperty(resultsState, 'userData', null)
    const sponsoredProducts = getAlgoliaUserDataProductsByType(
      userData,
      'labels'
    )
    const highlightedProducts = getAlgoliaUserDataProductsByType(
      userData,
      'highlighted'
    )

    return {
      sponsoredProducts,
      highlightedProducts,
      adServerMetadata,
      adServerSponsoredProducts: injectedSponsoredProducts,
    }
  }

  componentDidMount() {
    // Trigger initial page handlers
    store.dispatch({ type: SEARCH_RESULTS_HITS_UPDATED })
  }

  shouldComponentUpdate(nextProps) {
    const { queryID: currentQueryID, i18n, indexName } = this.props
    const { queryID: nextQueryID } = nextProps
    const { events } = store.getState()

    if (this.filteredItems.length === 0 && i18n) {
      this.filteredItems = getSortingItems({
        indexName,
        i18n,
        isSearchResultsPage: true,
      })
    }

    this.enableSponsoredLabel =
      !events?.searchState ||
      events?.searchState?.sortBy === this.filteredItems[0]?.value

    return nextQueryID !== currentQueryID
  }

  componentDidUpdate() {
    // Restore labels with modals
    store.dispatch({
      type: REFRESH_MODALS,
      selector: ALGOLIA_SEARCH_LIST_SELECTOR,
    })

    store.dispatch({ type: SEARCH_RESULTS_HITS_UPDATED })
  }

  shouldRenderAdSlotAtTheBottom = (entries, slotPosition) =>
    slotPosition >= entries.length

  getQueryParams(product, position) {
    const { query, queryID } = this.props
    const { objectID } = product

    return {
      eventType: GTM_EVENT_TYPE_CLICK,
      objectIDs: `[${objectID}]`,
      position,
      query,
      queryID,
    }
  }

  getSlotConditionals(entries) {
    const { firstAdSlotPosition, secondAdSlotPosition } = this.props

    const shouldRenderAdSlotAtTheBottom = () =>
      this.shouldRenderAdSlotAtTheBottom(entries, firstAdSlotPosition)
    const shouldRenderFirstAdSlot = index =>
      !shouldRenderAdSlotAtTheBottom() && index + 1 === firstAdSlotPosition
    const shouldRenderSecondAdSlot = index =>
      entries.length >= secondAdSlotPosition &&
      index + 1 === secondAdSlotPosition

    return {
      shouldRenderFirstAdSlot,
      shouldRenderSecondAdSlot,
      shouldRenderAdSlotAtTheBottom,
    }
  }

  getListEntries() {
    const {
      hits,
      publicRuntimeConfig: {
        publicConfig: { cdnUrl, superScripts, currency } = {},
        pageProperties: { experiments },
      },
      isFilterApplied,
      currentPage,
      hitsPerPage,
    } = this.props
    const { sponsoredProducts, highlightedProducts } = this.state

    const adServerSponsoredProducts = isFilterApplied
      ? []
      : this.state.adServerSponsoredProducts

    const adServerMetadata = isFilterApplied ? {} : this.state.adServerMetadata

    let products = []

    const maxPositionValue = (currentPage + 1) * hitsPerPage
    const minPositionValue = maxPositionValue - hitsPerPage

    if (!isFilterApplied && adServerSponsoredProducts?.length) {
      const injectedProductsForPage = adServerSponsoredProducts.filter(
        injectedSponsoredProduct => {
          const position = injectedSponsoredProduct?.position || 1

          return position > minPositionValue && position <= maxPositionValue
        }
      )

      if (injectedProductsForPage.length) {
        injectedProductsForPage.forEach(injectedProductForPage => {
          const position =
            adServerMetadata[injectedProductForPage.product.id]?.position
          const product = convertAlgoliaHitToProductProps({
            product: {
              ...injectedProductForPage.product,
              position,
            },
            superScripts,
            isRebrandingEnabled: isRebrandingITActive(experiments),
            currency,
          })
          product.cdnUrl = cdnUrl
          products.push(product)
        })
      }
    }

    hits.forEach(hit => {
      let id
      if (sponsoredProducts[hit.id]) {
        id = hit.id
      } else if (sponsoredProducts[hit.pzn]) {
        id = hit.pzn
      }

      const sponsored = Boolean(id)
      if (sponsored) {
        hit.sponsored = true
        hit.sponsoredProductLabel = sponsoredProducts[id]
      } else {
        // a product should not be highlighted if it is sponsored
        if (highlightedProducts[hit.id]) {
          id = hit.id
        } else if (highlightedProducts[hit.pzn]) {
          id = hit.pzn
        }

        if (id) {
          hit.highlighted = true
          hit.highlightedProductLabel = highlightedProducts[id]
        }
      }
      hit.cdnUrl = cdnUrl

      const productIsDuplicated = products.some(
        product => product.id === hit.id
      )
      const adServerMetadataContent = adServerMetadata?.[hit.id] || {}
      const alreadyInjectedOnAnotherPage =
        adServerSponsoredProducts?.some(injectedSponsoredProduct => {
          if (injectedSponsoredProduct.product.id !== hit.id) {
            return false
          }

          const position = injectedSponsoredProduct?.position || 1

          return !(position > minPositionValue && position <= maxPositionValue)
        }) || false

      if (
        !alreadyInjectedOnAnotherPage &&
        (!productIsDuplicated ||
          (productIsDuplicated && adServerMetadataContent.offer))
      ) {
        let metadataId = adServerMetadataContent
          ? adServerMetadataContent.metadataId
          : undefined
        let position = adServerMetadataContent
          ? adServerMetadataContent.position
          : undefined

        if (adServerMetadataContent.offer) {
          metadataId = undefined
          position = undefined
        }

        const convertedProduct = convertAlgoliaHitToProductProps({
          product: {
            ...hit,
            metadataId,
            position,
          },
          superScripts,
          isRebrandingEnabled: isRebrandingITActive(experiments),
          currency,
        })

        products.push(convertedProduct)
      }
    })

    const reorderedHits = reorderHitsBySponsoredProduct(products)

    return reorderedHits.filter(
      ({ stockStatus, name }) =>
        (isProductAvailable(stockStatus) || isProductOutOfStock(stockStatus)) &&
        name !== ''
    )
  }

  shouldRenderNewLayout() {
    const {
      publicRuntimeConfig: {
        publicConfig: { tenant },
        pageProperties: { pageType, experiments },
      },
    } = this.props

    switch (pageType) {
      case PAGE_TYPE_SEARCH_RESULTS:
        return true
      case PAGE_TYPE_CATEGORY:
      case PAGE_TYPE_SELLER_PRODUCTS:
        return isMarketplace(experiments, tenant) || tenant !== TENANT_IT
      case PAGE_TYPE_BRAND_PAGE:
        return isMarketplace(experiments, tenant)
      default:
        return false
    }
  }

  render() {
    const {
      isFilterApplied,
      publicRuntimeConfig: {
        publicConfig: { onlineDoctorConfig } = {},
        pageProperties: { adPlacements, experiments, pageType },
      },
    } = this.props

    let entries = this.getListEntries()
    if (!entries?.length) {
      return null
    }

    const adServerMetadata = isFilterApplied ? {} : this.state.adServerMetadata

    const {
      shouldRenderAdSlotAtTheBottom,
      shouldRenderFirstAdSlot,
      shouldRenderSecondAdSlot,
    } = this.getSlotConditionals(entries)

    const shouldRenderNewLayout = this.shouldRenderNewLayout()
    const adSlotProps = {
      position: 'center',
      wide: true,
      paddingTop: !shouldRenderNewLayout,
      noPadding: false,
      dataAttributes: { 'data-is-search-page': shouldRenderNewLayout },
    }
    const isBannerExperimentEnabled =
      isAdServerBannerExperimentEnabled(experiments)

    let topBannerPlacement

    let firstAdBanner
    let secondAdBanner

    if (isBannerExperimentEnabled) {
      topBannerPlacement = adPlacements?.find(
        item => item?.divName === AD_SLOT_SEARCH_TOP_BANNER
      )

      const firstAdBannerPlacement = adPlacements?.find(
        item => item?.divName === AD_SLOT_SEARCH_HITS_1
      )

      if (firstAdBannerPlacement) {
        firstAdBanner = (
          <AdBanner
            placement={firstAdBannerPlacement}
            id={AD_SLOT_SEARCH_HITS_1}
            renderBannerOnServerSide
          />
        )
      }

      const secondAdBannerPlacement = adPlacements?.find(
        item => item?.divName === AD_SLOT_SEARCH_HITS_2
      )

      if (secondAdBannerPlacement) {
        secondAdBanner = (
          <AdBanner
            placement={secondAdBannerPlacement}
            id={AD_SLOT_SEARCH_HITS_2}
            renderBannerOnServerSide
          />
        )
      }
    } else {
      firstAdBanner = <AdSlot id={AD_SLOT_SEARCH_HITS_1} {...adSlotProps} />
      secondAdBanner = <AdSlot id={AD_SLOT_SEARCH_HITS_2} {...adSlotProps} />
    }

    const { variant } = getExperiment(NEW_SEARCH_PAGE_LAYOUT_V3, experiments)

    const isCompactSerpEnabled =
      [EXPERIMENT_VARIATION.V1].includes(variant) &&
      pageType === PAGE_TYPE_SEARCH_RESULTS

    return (
      <>
        {topBannerPlacement && (
          <AdBanner
            placement={topBannerPlacement}
            id={AD_SLOT_SEARCH_TOP_BANNER}
            renderBannerOnServerSide
            className="o-Hits__TopSlot"
          />
        )}
        <ul
          className={clsx(HITS_BASE_CLASSNAME, 'a-list-reset', {
            'u-margin-large--bottom': shouldRenderAdSlotAtTheBottom(),
            'o-Hits--compact': isCompactSerpEnabled,
          })}
          data-clientside-hook={ALGOLIA_SEARCH_LIST_HOOK}
        >
          {entries.map((product, index) => {
            const lazyLoadImage = index >= IMAGE_LAZY_LOAD_START_INDEX
            const renderFirstAdSlot = shouldRenderFirstAdSlot(index)
            const renderSecondAdSlot = shouldRenderSecondAdSlot(index)

            const queryParams = this.getQueryParams(product, index + 1)

            let item = (
              <FilteredProductListItem
                {...queryParams}
                adServerMetadata={adServerMetadata}
                lazyLoadImage={lazyLoadImage}
                product={product}
                queryParams={queryParams}
              />
            )

            if (shouldRenderNewLayout) {
              item = (
                <SearchProductListItem
                  adServerMetadata={adServerMetadata}
                  enableSponsoredLabel={this.enableSponsoredLabel}
                  isFirst={index === 0}
                  lazyLoadImage={lazyLoadImage}
                  product={product}
                  queryParams={queryParams}
                />
              )
            }

            if (isCompactSerpEnabled) {
              item = (
                <li>
                  <CompactProductListItem
                    adServerMetadata={adServerMetadata}
                    product={product}
                    queryParams={queryParams}
                    enableSponsoredLabel
                  />
                </li>
              )
            }

            return (
              <Fragment key={product.metadataId ?? product.id}>
                {item}
                <>
                  {renderFirstAdSlot && firstAdBanner && (
                    <li
                      className={clsx('u-overflow-hidden', {
                        'o-Hits--compact-adSlot': isCompactSerpEnabled,
                      })}
                    >
                      {firstAdBanner}
                    </li>
                  )}
                  {renderSecondAdSlot && secondAdBanner && (
                    <li
                      className={clsx('u-overflow-hidden', {
                        'o-Hits--compact-adSlot': isCompactSerpEnabled,
                      })}
                    >
                      {secondAdBanner}
                    </li>
                  )}
                </>
              </Fragment>
            )
          })}
        </ul>
        {onlineDoctorConfig?.enabled && (
          <>
            <OdsTransitionModal pageType={pageType} />
            <Modal
              id={`modalIdOdsSERP`}
              size="medium"
              title={<Trans id="pdp.buyboxwithvariants.odsModal.title" />}
            >
              <OdsModalContent />
            </Modal>
          </>
        )}
        {pageType !== PAGE_TYPE_CATEGORY && <SellerModal />}
        {shouldRenderAdSlotAtTheBottom() && firstAdBanner}
        <LoadMoreButtonExperiment {...this.props} />
      </>
    )
  }
}

Hits.propTypes = {
  currentPage: number.isRequired,
  firstAdSlotPosition: number,
  hasMore: bool,
  hits: arrayOf(shape(hitsType)).isRequired,
  hitsPerPage: number.isRequired,
  i18n: i18nType,
  indexName: string.isRequired,
  isFilterApplied: bool,
  publicRuntimeConfig: shape(publicRuntimeConfigType).isRequired,
  query: string,
  queryID: string.isRequired,
  refineNext: func,
  secondAdSlotPosition: number,
}

Hits.defaultProps = {
  firstAdSlotPosition: 3,
  hasMore: false,
  isFilterApplied: false,
  query: '',
  refineNext: noop,
  secondAdSlotPosition: 7,
}
